import type React from "react";
import { Fragment, Suspense, useCallback, useEffect, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { Link, useSearchParams } from "react-router-dom";
import type { TikTokAdsAdvertiser } from "shared";
import { useBillingEntitlements } from "../billing/useSubscription";
import { AlertType, useAlertEmit } from "../common/alert/Alert";
import { Icon, IconSVG } from "../common/Icon";
import { useModal } from "../common/modal/Modal";
import { useCompany } from "../company/useCompany";
import { Button } from "../components/Button";
import { Select } from "../components/Select";
import { useFeatureFlag } from "../hooks/use-app-metadata";
import { LinkGen } from "../LinkGen";
import { useCanManageMetaAds } from "../meta-ads/useMyMetaAdsRole";
import { CanvaSettingsPanel } from "./CanvaSettingsPanel";
import { HeyGenAppsCard } from "./HeyGenAppsCard";
import { ModalConnectWordpress } from "./ModalConnectWordpress";
import { ShowChangePlatformProfileName } from "./PageSettings";
import {
	useCanva,
	useCanvaActions,
	useFacebook,
	useFacebookActions,
	useGoogleAnalytics,
	useGoogleAnalyticsActions,
	useGoogleMyBusiness,
	useGoogleMyBusinessActions,
	useGoogleSearchConsole,
	useGoogleSearchConsoleActions,
	useInstagram,
	useInstagramActions,
	useLinkedin,
	useLinkedinActions,
	useMetaAds,
	useMetaAdsActions,
	useThreads,
	useThreadsActions,
	useTikTok,
	useTikTokActions,
	useTikTokAds,
	useTikTokAdsActions,
	useWix,
	useWixActions,
	useWordpress,
	useWordpressActions,
	useX,
	useXActions,
	useYouTube,
	useYouTubeActions,
} from "./useIntegrations";
import { useXAdminCheck } from "./useXAdminSpend";
import { VideoCreationCard } from "./VideoCreationCard";

const DisconnectSwitch = (props: {
	connected: boolean;
	loading: boolean;
	onDisconnect: () => unknown;
}) => {
	const handleToggle = (e: React.MouseEvent<HTMLInputElement>) => {
		e.preventDefault();
		props.onDisconnect();
	};

	if (!props.connected) {
		return null;
	}

	return (
		<Form.Check
			type="switch"
			disabled={props.loading}
			onClick={handleToggle}
			checked
			className="app-connect-switch"
		/>
	);
};

type YouTubeOAuthReason =
	| "denied"
	| "insufficient_permissions"
	| "no_channel"
	| "missing_code"
	| "oauth_error"
	| "exchange_failed"
	| string;

function getYouTubeOAuthErrorCopy(
	reason: YouTubeOAuthReason | null,
	detail: string | null,
) {
	switch (reason) {
		case "denied":
			return {
				title: "YouTube authorization was cancelled",
				message:
					detail ||
					"You cancelled Google login or denied the requested permissions.",
			};
		case "insufficient_permissions":
			return {
				title: "Not enough YouTube permissions",
				message:
					detail ||
					"Grant YouTube upload and channel access when Google asks, then try again.",
			};
		case "no_channel":
			return {
				title: "No YouTube channel found",
				message: detail || "Choose a YouTube channel in Google and try again.",
			};
		case "missing_code":
			return {
				title: "Could not finish YouTube connection",
				message:
					detail ||
					"Google did not return an authorization code. Please try again.",
			};
		case "oauth_error":
			return {
				title: "Google returned an authorization error",
				message:
					detail ||
					"Google rejected the authorization request before YouTube could be connected.",
			};
		default:
			return {
				title: "Could not connect YouTube",
				message:
					detail ||
					"Something went wrong while connecting YouTube. Please try again.",
			};
	}
}

const YouTubeAppsCard = (props: {
	oauthError: { reason: string | null; detail: string | null } | null;
	onDismissOAuthError: () => void;
}) => {
	const { status } = useYouTube();
	const { connect, connectAnother, disconnect, disconnectChannel, setChannel } =
		useYouTubeActions();
	const [retrying, setRetrying] = useState(false);

	const connected = status.data.connected === true;
	const tokenExpired = !connected && status.data.tokenExpired === true;
	const channels =
		status.data.channels ?? (status.data.profile ? [status.data.profile] : []);
	const errorCopy = props.oauthError
		? getYouTubeOAuthErrorCopy(props.oauthError.reason, props.oauthError.detail)
		: null;

	const handleRetry = async () => {
		setRetrying(true);
		try {
			props.onDismissOAuthError();
			if (connected) {
				await connectAnother();
			} else {
				await connect();
			}
		} catch {
			setRetrying(false);
		}
	};

	return (
		<div className="app-connect">
			<div className="app-connect-header">
				<div className="app-connect-metadata">
					<Icon name={IconSVG.YouTube} size={38} />
					<div>
						<div>YouTube</div>
						<div>Video posts</div>
					</div>
				</div>
				{connected ? (
					<DisconnectSwitch
						connected
						loading={disconnect.isPending}
						onDisconnect={async () => await disconnect.mutateAsync()}
					/>
				) : null}
			</div>
			<div className="app-connect-spacer" />
			{errorCopy ? (
				<div className="app-connect-oauth-error" role="alert">
					<div className="app-connect-oauth-error__title">
						{errorCopy.title}
					</div>
					<p className="app-connect-oauth-error__message">
						{errorCopy.message}
					</p>
					<Button loading={retrying} onClick={handleRetry} full>
						Try again
					</Button>
				</div>
			) : null}
			{connected ? (
				<div className="app-connect-youtube-channels">
					<p className="app-connect-youtube-channels__title">
						Connected channels
					</p>
					<ul className="app-connect-youtube-channels__list">
						{channels.map((channel) => {
							const isDefault = status.data.profile?.id === channel.id;
							const initials = channel.name
								.trim()
								.split(/\s+/)
								.slice(0, 2)
								.map((part) => part[0]?.toUpperCase() ?? "")
								.join("");

							return (
								<li
									key={channel.id}
									className="app-connect-youtube-channels__item"
								>
									<div className="app-connect-youtube-channels__info">
										{channel.imageUrl ? (
											<img
												src={channel.imageUrl}
												alt=""
												className="app-connect-youtube-channels__avatar"
											/>
										) : (
											<div
												className="app-connect-youtube-channels__avatar-fallback"
												aria-hidden
											>
												{initials || "YT"}
											</div>
										)}
										<div className="app-connect-youtube-channels__meta">
											<div
												className="app-connect-youtube-channels__name"
												title={channel.name}
											>
												{channel.name}
											</div>
											{isDefault ? (
												<span className="app-connect-youtube-channels__badge">
													Default
												</span>
											) : null}
										</div>
									</div>
									<div className="app-connect-youtube-channels__actions">
										{!isDefault ? (
											<Button
												size="sm"
												variant="secondary"
												loading={setChannel.isPending}
												onClick={async () => {
													await setChannel.mutateAsync({
														id: channel.id,
													});
												}}
											>
												Set default
											</Button>
										) : null}
										<Button
											size="sm"
											variant="ghost"
											loading={disconnectChannel.isPending}
											onClick={async () => {
												await disconnectChannel.mutateAsync(channel.id);
											}}
										>
											Remove
										</Button>
									</div>
								</li>
							);
						})}
					</ul>
					<Button onClick={connectAnother} full>
						Add another channel
					</Button>
					<p className="app-connect-youtube-channels__hint">
						Each YouTube channel needs its own Google authorization. Choose the
						other channel when Google asks during connect.
					</p>
				</div>
			) : !errorCopy ? (
				<Button onClick={connect} full>
					{tokenExpired ? "Reconnect" : "Connect"}
				</Button>
			) : null}
		</div>
	);
};

const TikTokAdsAppsCard = () => {
	const { status } = useTikTokAds();
	const { connect, disconnect, getAdvertisers, setAdvertiser } =
		useTikTokAdsActions();
	const [advertisers, setAdvertisers] = useState<TikTokAdsAdvertiser[] | null>(
		null,
	);

	const { connected, advertiser, businessAccount, sandboxMode } = status.data;
	const needsReconnect =
		status.data.status === "requires_reconnection" ||
		(!sandboxMode && businessAccount?.tokenExpired === true);

	return (
		/** biome-ignore lint/a11y/useAriaPropsSupportedByRole: baseline */
		<div className="app-connect" aria-label="TikTok Ads and Comments">
			<div className="app-connect-header">
				<div className="app-connect-metadata">
					<Icon name={IconSVG.TikTok} size={38} />
					<div>
						<div>TikTok</div>
						<div>
							Ads &amp; Comments
							{sandboxMode ? " (Sandbox)" : ""}
						</div>
					</div>
				</div>
				<DisconnectSwitch
					connected={connected}
					loading={disconnect.isPending}
					onDisconnect={async () => {
						if (
							!window.confirm(
								"Disconnecting will revoke TikTok Ads and inbox comment access. Campaigns can no longer be managed from this platform, but historical data remains visible. Continue?",
							)
						) {
							return;
						}
						await disconnect.mutateAsync();
					}}
				/>
			</div>
			<div className="app-connect-spacer" />
			{sandboxMode && (
				<div className="app-connect-page-selection">
					Environment
					<span className="active">Sandbox — ads API only</span>
				</div>
			)}
			{connected ? (
				<>
					<div className="app-connect-page-selection">
						Advertiser
						<span className="active">{advertiser?.name ?? "Not selected"}</span>
						<Button
							onClick={async () => setAdvertisers(await getAdvertisers())}
							variant="secondary"
							size="sm"
						>
							Change
						</Button>
					</div>
					{advertisers && (
						<Select
							className="form-select"
							value={advertiser?.id ?? ""}
							disabled={setAdvertiser.isPending}
							onChange={async (e) => {
								await setAdvertiser.mutateAsync(e.target.value);
								setAdvertisers(null);
							}}
						>
							<option value="" disabled>
								Select an advertiser account
							</option>
							{advertisers.map((item) => (
								<option key={item.id} value={item.id}>
									{item.name}
									{item.currency ? ` (${item.currency})` : ""}
								</option>
							))}
						</Select>
					)}
					{!sandboxMode && (
						<>
							<div className="app-connect-page-selection">
								Comments account
								<span className="active">
									{businessAccount?.connected
										? (businessAccount.name ?? "Connected")
										: businessAccount?.tokenExpired
											? "Reconnect required"
											: "Not authorized"}
								</span>
							</div>
							{(!businessAccount?.connected ||
								businessAccount?.tokenExpired) && (
								<Button onClick={connect} full variant="secondary">
									Reconnect
								</Button>
							)}
						</>
					)}
				</>
			) : (
				<Button onClick={connect} full>
					{needsReconnect ? "Reconnect" : "Connect"}
				</Button>
			)}
		</div>
	);
};

const MetaAdsAppsCard = () => {
	const canManage = useCanManageMetaAds();
	const { status: metaAdsStatus } = useMetaAds();
	const {
		connect: metaAdsConnect,
		disconnect: metaAdsDisconnect,
		getAdAccount: metaAdsGetAdAccount,
		getAdAccounts: metaAdsGetAdAccounts,
		setAdAccount: metaAdsSetAdAccount,
		getPage: metaAdsGetPage,
		getPages: metaAdsGetPages,
		setPage: metaAdsSetPage,
	} = useMetaAdsActions();

	return (
		/** biome-ignore lint/a11y/useAriaPropsSupportedByRole: baseline */
		<div className="app-connect" aria-label="Meta Ads">
			<div className="app-connect-header">
				<div className="app-connect-metadata">
					<Icon name={IconSVG.Facebook} size={38} />
					<div>
						<div>Meta</div>
						<div>Ads</div>
					</div>
				</div>
				{canManage && (
					<DisconnectSwitch
						connected={metaAdsStatus.data.connected}
						loading={metaAdsDisconnect.isPending}
						onDisconnect={async () => {
							if (
								!window.confirm(
									"Disconnecting will revoke Meta access. Active campaigns can no longer be managed from this platform, but historical data remains visible. Continue?",
								)
							) {
								return;
							}
							await metaAdsDisconnect.mutateAsync();
						}}
					/>
				)}
			</div>
			<div className="app-connect-spacer" />
			{metaAdsStatus.data.connected ? (
				<>
					<div className="app-connect-page-selection">
						Ad Account
						<span className="active">
							<ShowChangePlatformProfileName
								profile={metaAdsGetAdAccount.data ?? undefined}
								onEdit={
									canManage ? metaAdsGetAdAccounts.mutateAsync : undefined
								}
								onSelect={
									canManage ? metaAdsSetAdAccount.mutateAsync : undefined
								}
							/>
							{metaAdsGetAdAccount.data?.id && (
								<div className="text-muted" style={{ fontSize: 12 }}>
									ID: {metaAdsGetAdAccount.data.id}
								</div>
							)}
						</span>
					</div>
					<div className="app-connect-page-selection">
						Facebook Page
						<span className="active">
							<ShowChangePlatformProfileName
								profile={metaAdsGetPage.data}
								onEdit={canManage ? metaAdsGetPages.mutateAsync : undefined}
								onSelect={canManage ? metaAdsSetPage.mutateAsync : undefined}
								emptyLabel="Select a Page for Instant Forms & leads"
							/>
							{metaAdsGetPage.data?.id && (
								<div className="text-muted" style={{ fontSize: 12 }}>
									ID: {metaAdsGetPage.data.id}
								</div>
							)}
						</span>
					</div>
				</>
			) : canManage && metaAdsStatus.data.tokenExpired ? (
				<Button onClick={metaAdsConnect} full>
					Reconnect
				</Button>
			) : canManage ? (
				<Button onClick={metaAdsConnect} full>
					Connect
				</Button>
			) : (
				<p className="text-muted" style={{ marginBottom: 0 }}>
					A Meta Ads Manager needs to connect this account.
				</p>
			)}
		</div>
	);
};

export const TabApps = () => {
	const { status: fbStatus } = useFacebook();
	const {
		connect: fbConnect,
		disconnect,
		setProfile,
		getProfiles,
	} = useFacebookActions();
	const { status: igStatus } = useInstagram();
	const {
		connect: igConnect,
		disconnect: igDisconnect,
		getProfiles: igGetProfiles,
		setProfile: igSetProfile,
	} = useInstagramActions();
	const { status: thStatus } = useThreads();
	const { connect: thConnect, disconnect: thDisconnect } = useThreadsActions();
	const { status: lnStatus } = useLinkedin();
	const {
		connect: lnConnect,
		disconnect: lnDisconnect,
		getProfiles: lnGetProfiles,
		setProfile: lnSetProfile,
	} = useLinkedinActions();
	const { status: wpStatus } = useWordpress();
	const { disconnect: wpDisconnect } = useWordpressActions();
	const { status: wixStatus } = useWix();
	const { connect: wixConnect, disconnect: wixDisconnect } = useWixActions();
	const { open, close } = useModal();
	const company = useCompany();
	const [searchParams, setSearchParams] = useSearchParams();
	const emit = useAlertEmit();
	const [youtubeOAuthError, setYouTubeOAuthError] = useState<{
		reason: string | null;
		detail: string | null;
	} | null>(null);

	useEffect(() => {
		const youtubeStatus = searchParams.get("youtube");
		if (youtubeStatus === "error") {
			setYouTubeOAuthError({
				reason: searchParams.get("reason"),
				detail: searchParams.get("detail"),
			});
			searchParams.delete("youtube");
			searchParams.delete("reason");
			searchParams.delete("detail");
			setSearchParams(searchParams, { replace: true });
			return;
		}

		const threadsStatus = searchParams.get("threads");
		if (threadsStatus) {
			const detail = searchParams.get("threadsDetail");
			for (const key of [
				"threads",
				"threadsStep",
				"threadsDetail",
				"threadsHttp",
				"threadsMetaCode",
				"threadsMetaSubcode",
				"threadsMetaType",
				"threadsMetaTitle",
				"threadsMetaMsg",
				"threadsFbtrace",
				"threadsRedirectUri",
				"threadsClientId",
				"threadsCodeLength",
			]) {
				searchParams.delete(key);
			}
			const error = searchParams.get("error");
			if (error) {
				emit({ type: AlertType.ERROR, content: error });
				searchParams.delete("error");
			} else if (threadsStatus === "error") {
				emit({
					type: AlertType.ERROR,
					content: detail || "Failed to connect Threads. Please try again.",
				});
			} else if (threadsStatus === "success") {
				emit({
					type: AlertType.SUCCESS,
					content: "Threads connected successfully",
				});
			}
			setSearchParams(searchParams, { replace: true });
			return;
		}

		const error = searchParams.get("error");
		if (error) {
			emit({ type: AlertType.ERROR, content: error });
			searchParams.delete("error");
			setSearchParams(searchParams, { replace: true });
		}
	}, [searchParams, setSearchParams, emit]);

	const handleOpenConnectWordpressModal = useCallback(
		(initialWebsite?: string) => {
			open(ModalConnectWordpress, {
				initialWebsite,
				onSuccess: close,
			});
		},
		[open, close],
	);

	const isGoogleSearchEnabled = useFeatureFlag(
		"GoogleSearchConsoleIntegration",
	);
	const isGoogleBusinessEnabled = useFeatureFlag("GoogleMyBusinessIntegration");
	const isGoogleAnalyticsEnabled = useFeatureFlag("GoogleAnalyticsIntegration");
	const isMetaAdsEnabled = useFeatureFlag("MetaAdsIntegration");
	const isYouTubeEnabled = useFeatureFlag("YouTubeIntegration");
	const isTikTokEnabled = useFeatureFlag("TikTokIntegration");
	const isTikTokAdsEnabled = useFeatureFlag("TikTokAdsIntegration");
	const isCanvaEnabled = useFeatureFlag("CanvaIntegration");
	const isHeyGenEnabled = useFeatureFlag("HeyGenIntegration");
	const isXEnabled = useFeatureFlag("XIntegration");
	const { data: xAdminCheck } = useXAdminCheck();

	const { status: googleSearchConsoleStatus } = useGoogleSearchConsole();

	const {
		connect: googleSearchConsoleConnect,
		disconnect: googleSearchConsoleDisconnect,
		getWebsite: googleSearchConsoleGetWebsite,
		getWebsites: googleSearchConsoleGetWebsites,
		setWebsite: googleSearchConsoleSetWebsite,
	} = useGoogleSearchConsoleActions();

	const { status: googleMyBusinessStatus } = useGoogleMyBusiness();

	const {
		connect: googleMyBusinessConnect,
		disconnect: googleMyBusinessDisconnect,
		getAccounts: googleMyBusinessGetAccounts,
		getAccount: googleMyBusinessGetAccount,
		setAccount: googleMyBusinessSetAccount,
		resyncAccount: googleMyBusinessResyncAccount,
	} = useGoogleMyBusinessActions();

	const { status: googleAnalyticsStatus } = useGoogleAnalytics();

	const {
		connect: googleAnalyticsConnect,
		disconnect: googleAnalyticsDisconnect,
		getProperty: googleAnalyticsGetProperty,
		getProperties: googleAnalyticsGetProperties,
		setProperty: googleAnalyticsSetProperty,
	} = useGoogleAnalyticsActions();

	const { status: tiktokStatus } = useTikTok();
	const { connect: tiktokConnect, disconnect: tiktokDisconnect } =
		useTikTokActions();
	const { status: canvaStatus } = useCanva();
	const { connect: canvaConnect, disconnect: canvaDisconnect } =
		useCanvaActions();
	const xStatus = useX();
	const { connect: xConnect, disconnect: xDisconnect } = useXActions();
	const { data: xEntitlements } = useBillingEntitlements();

	return (
		<Row>
			<Col xs={12}>
				<h2 className="mb-4">Connect Your Social Media Accounts</h2>
				<p>
					Manage all your social media accounts from one place. Click the
					"Connect" button to add a platform. <br />
					To disconnect from platform, use the switch. When connected use
					“Change page” button to switch between pages.
				</p>
			</Col>
			<Col xs={12}>
				<div className="app-connect-list">
					{/*Facebook*/}
					{/** biome-ignore lint/a11y/useAriaPropsSupportedByRole: baseline */}
					<div className="app-connect" aria-label="Facebook (Meta)">
						<div className="app-connect-header">
							<div className="app-connect-metadata">
								<Icon name={IconSVG.Facebook} size={38} />
								Facebook (Meta)
							</div>
							<DisconnectSwitch
								connected={fbStatus.data.connected}
								loading={disconnect.isPending}
								onDisconnect={() => disconnect.mutate()}
							/>
						</div>
						<div className="app-connect-spacer" />
						{fbStatus.data.connected ? (
							<Fragment>
								<div className="app-connect-page-selection">
									Page
									<span className="active">
										<ShowChangePlatformProfileName
											profile={fbStatus.data.profile}
											onEdit={getProfiles.mutateAsync}
											onSelect={setProfile.mutateAsync}
										/>
									</span>
								</div>
							</Fragment>
						) : (
							<Button onClick={fbConnect} full>
								Connect
							</Button>
						)}
					</div>
					{/*Instagram*/}
					{/** biome-ignore lint/a11y/useAriaPropsSupportedByRole: baseline */}
					<div className="app-connect" aria-label="Instagram (Meta)">
						<div className="app-connect-header">
							<div className="app-connect-metadata">
								<Icon name={IconSVG.Instagram} size={38} />
								Instagram (Meta)
							</div>
							<DisconnectSwitch
								connected={igStatus.data.connected}
								loading={igDisconnect.isPending}
								onDisconnect={() => igDisconnect.mutate()}
							/>
						</div>
						<div className="app-connect-spacer" />
						{igStatus.data.connected ? (
							<Fragment>
								<div className="app-connect-page-selection">
									Profile
									<span className="active">
										<ShowChangePlatformProfileName
											profile={igStatus.data.profile}
											onEdit={igGetProfiles.mutateAsync}
											onSelect={igSetProfile.mutateAsync}
										/>
									</span>
								</div>
							</Fragment>
						) : (
							<Button onClick={igConnect} full>
								Connect
							</Button>
						)}
					</div>
					{/*Threads*/}
					{/** biome-ignore lint/a11y/useAriaPropsSupportedByRole: baseline */}
					<div className="app-connect" aria-label="Threads (Meta)">
						<div className="app-connect-header">
							<div className="app-connect-metadata">
								<Icon name={IconSVG.BrandThreads} size={38} />
								Threads (Meta)
							</div>
							<DisconnectSwitch
								connected={thStatus.data.connected}
								loading={thDisconnect.isPending}
								onDisconnect={() => thDisconnect.mutate()}
							/>
						</div>
						<div className="app-connect-spacer" />
						{!thStatus.data.connected ? (
							<Button full onClick={thConnect}>
								Connect
							</Button>
						) : (
							<div className="app-connect-page-selection">
								Profile
								<span className="active">{thStatus.data.profile?.name}</span>
							</div>
						)}
					</div>
					{/*LinkedIn*/}
					{/** biome-ignore lint/a11y/useAriaPropsSupportedByRole: baseline */}
					<div className="app-connect" aria-label="LinkedIn">
						<div className="app-connect-header">
							<div className="app-connect-metadata">
								<Icon name={IconSVG.LinkedIn} size={38} />
								LinkedIn
							</div>
							<DisconnectSwitch
								connected={lnStatus.data.connected}
								loading={lnDisconnect.isPending}
								onDisconnect={() => lnDisconnect.mutate()}
							/>
						</div>
						<div className="app-connect-spacer" />
						{lnStatus.data.connected ? (
							<Fragment>
								<div className="app-connect-page-selection">
									Profile
									<span className="active">
										<ShowChangePlatformProfileName
											profile={lnStatus.data.profile}
											onEdit={lnGetProfiles.mutateAsync}
											onSelect={lnSetProfile.mutateAsync}
										/>
									</span>
								</div>
							</Fragment>
						) : (
							<Button onClick={lnConnect} full>
								Connect
							</Button>
						)}
					</div>
					<div className="app-connect">
						<div className="app-connect-header">
							<div className="app-connect-metadata">
								<Icon name={IconSVG.Wordpress} size={38} />
								Wordpress
							</div>
							<DisconnectSwitch
								connected={wpStatus.data.connected}
								loading={wpDisconnect.isPending}
								onDisconnect={() => wpDisconnect.mutate()}
							/>
						</div>
						<div className="app-connect-spacer" />
						{wpStatus.data.connected ? (
							<Fragment>
								<div className="app-connect-page-selection">
									Website name
									<span className="active">{wpStatus.data.profile?.name}</span>
								</div>
								<div className="app-connect-page-selection">
									Website URL
									<span className="active">{wpStatus.data.profile?.id}</span>
								</div>
							</Fragment>
						) : (
							<Button
								onClick={() => handleOpenConnectWordpressModal(company.website)}
								full
							>
								Connect
							</Button>
						)}
					</div>
					<div className="app-connect">
						<div className="app-connect-header">
							<div className="app-connect-metadata">
								<Icon name={IconSVG.Wix} size={38} />
								Wix
							</div>
							<DisconnectSwitch
								connected={wixStatus.data.connected}
								loading={wixDisconnect.isPending}
								onDisconnect={() => wixDisconnect.mutate()}
							/>
						</div>
						<div className="app-connect-spacer" />
						{wixStatus.data.connected ? (
							<Fragment>
								<div className="app-connect-page-selection">
									Site name
									<span className="active">{wixStatus.data.profile?.name}</span>
								</div>
								<div className="app-connect-page-selection">
									Site URL
									<span className="active">{wixStatus.data.profile?.id}</span>
								</div>
							</Fragment>
						) : (
							<Button
								onClick={() => wixConnect.mutate()}
								loading={wixConnect.isPending}
								full
							>
								Connect
							</Button>
						)}
					</div>
					{isGoogleSearchEnabled && (
						<div className="app-connect">
							<div className="app-connect-header">
								<div className="app-connect-metadata">
									<Icon name={IconSVG.Google} size={38} />
									<div>
										<div>Google</div>
										<div>Search Console</div>
									</div>
								</div>
								<DisconnectSwitch
									connected={googleSearchConsoleStatus.data.connected}
									loading={googleSearchConsoleDisconnect.isPending}
									onDisconnect={async () =>
										await googleSearchConsoleDisconnect.mutateAsync()
									}
								/>
							</div>
							<div className="app-connect-spacer" />
							{googleSearchConsoleStatus.data.connected ? (
								<div className="app-connect-page-selection">
									Profile
									<span className="active">
										<ShowChangePlatformProfileName
											profile={
												googleSearchConsoleGetWebsite.data ?? {
													id: "-1",
													name:
														googleSearchConsoleStatus.data.profile?.name ??
														"set profile",
												}
											}
											onEdit={googleSearchConsoleGetWebsites.mutateAsync}
											onSelect={googleSearchConsoleSetWebsite.mutateAsync}
										/>
									</span>
								</div>
							) : (
								<Button onClick={googleSearchConsoleConnect} full>
									Connect
								</Button>
							)}
						</div>
					)}
					{isGoogleBusinessEnabled && (
						<div className="app-connect">
							<div className="app-connect-header">
								<div className="app-connect-metadata">
									<Icon name={IconSVG.Google} size={38} />
									<div>
										<div>Google</div>
										<div>My Business</div>
									</div>
								</div>
								<DisconnectSwitch
									connected={googleMyBusinessStatus.data.connected}
									loading={googleMyBusinessDisconnect.isPending}
									onDisconnect={async () =>
										await googleMyBusinessDisconnect.mutateAsync()
									}
								/>
							</div>
							<div className="app-connect-spacer" />
							{googleMyBusinessStatus.data.connected ? (
								<>
									<div className="app-connect-page-selection">
										<span className="active">
											<ShowChangePlatformProfileName
												profile={googleMyBusinessGetAccount.data ?? undefined}
												onEdit={googleMyBusinessGetAccounts.mutateAsync}
												onSelect={googleMyBusinessSetAccount.mutateAsync}
											/>
										</span>
									</div>
									<Button
										variant="ghost"
										loading={googleMyBusinessResyncAccount.isPending}
										onClick={() => googleMyBusinessResyncAccount.mutateAsync()}
									>
										Re-sync account
									</Button>
								</>
							) : (
								<Button onClick={googleMyBusinessConnect} full>
									Connect
								</Button>
							)}
						</div>
					)}
					{isGoogleAnalyticsEnabled && (
						<div className="app-connect">
							<div className="app-connect-header">
								<div className="app-connect-metadata">
									<Icon name={IconSVG.Google} size={38} />
									<div>
										<div>Google</div>
										<div>Analytics</div>
									</div>
								</div>
								<DisconnectSwitch
									connected={googleAnalyticsStatus.data.connected}
									loading={googleAnalyticsDisconnect.isPending}
									onDisconnect={async () =>
										await googleAnalyticsDisconnect.mutateAsync()
									}
								/>
							</div>
							<div className="app-connect-spacer" />
							{googleAnalyticsStatus.data.connected ? (
								<div className="app-connect-page-selection">
									Property
									<span className="active">
										<ShowChangePlatformProfileName
											profile={
												googleAnalyticsGetProperty.data ?? {
													id: "-1",
													name:
														googleAnalyticsStatus.data.profile?.name ??
														"set property",
												}
											}
											onEdit={googleAnalyticsGetProperties.mutateAsync}
											onSelect={googleAnalyticsSetProperty.mutateAsync}
										/>
									</span>
								</div>
							) : (
								<Button onClick={googleAnalyticsConnect} full>
									Connect
								</Button>
							)}
						</div>
					)}
					{isMetaAdsEnabled && (
						<Suspense
							fallback={<div className="app-connect">Loading Meta Ads...</div>}
						>
							<MetaAdsAppsCard />
						</Suspense>
					)}
					{isYouTubeEnabled && (
						<YouTubeAppsCard
							oauthError={youtubeOAuthError}
							onDismissOAuthError={() => setYouTubeOAuthError(null)}
						/>
					)}
					{isTikTokEnabled && (
						<div className="app-connect">
							<div className="app-connect-header">
								<div className="app-connect-metadata">
									<Icon name={IconSVG.TikTok} size={38} />
									<div>
										<div>TikTok</div>
										<div>Video posts</div>
									</div>
								</div>
								<DisconnectSwitch
									connected={tiktokStatus.data.connected}
									loading={tiktokDisconnect.isPending}
									onDisconnect={async () =>
										await tiktokDisconnect.mutateAsync()
									}
								/>
							</div>
							<div className="app-connect-spacer" />
							{tiktokStatus.data.connected ? (
								<div className="app-connect-page-selection">
									Profile
									<span className="active">
										{tiktokStatus.data.profile?.name}
									</span>
								</div>
							) : tiktokStatus.data.tokenExpired ? (
								<Button onClick={tiktokConnect} full>
									Reconnect
								</Button>
							) : (
								<Button onClick={tiktokConnect} full>
									Connect
								</Button>
							)}
						</div>
					)}

					{isXEnabled && (
						<div className="app-connect">
							<div className="app-connect-header">
								<div className="app-connect-metadata">
									<Icon name={IconSVG.BrandX} size={38} />
									<div>X (Twitter)</div>
								</div>
								<DisconnectSwitch
									connected={xStatus.connected}
									loading={xDisconnect.isPending}
									onDisconnect={async () => await xDisconnect.mutateAsync()}
								/>
							</div>
							<div className="app-connect-spacer" />
							{xStatus.connected ? (
								<div className="app-connect-page-selection">
									<span>@{xStatus.profile?.name}</span>
								</div>
							) : !xEntitlements.xIntegration ? (
								<>
									<Button
										full
										onClick={() => {
											emit({
												type: AlertType.WARNING,
												content:
													"Activate the X Integration add-on in Billing first, then try to connect.",
											});
										}}
									>
										Activate add-on first
									</Button>
									<div style={{ marginTop: 8 }}>
										<Link to={LinkGen.Settings.Billing()}>Go to Billing</Link>
									</div>
								</>
							) : xStatus.tokenExpired ? (
								<Button onClick={xConnect} full>
									Reconnect
								</Button>
							) : (
								<Button onClick={xConnect} full>
									Connect
								</Button>
							)}
							{xAdminCheck?.isAdmin && (
								<div style={{ marginTop: 12 }}>
									<Link to={LinkGen.Settings.XSpendAdmin()}>
										View X API spend (admin)
									</Link>
								</div>
							)}
						</div>
					)}

					{isTikTokAdsEnabled && (
						<Suspense
							fallback={
								<div className="app-connect">Loading TikTok Ads...</div>
							}
						>
							<TikTokAdsAppsCard />
						</Suspense>
					)}

					{isCanvaEnabled && (
						/** biome-ignore lint/a11y/useAriaPropsSupportedByRole: baseline */
						<div className="app-connect" aria-label="Canva">
							<div className="app-connect-header">
								<div className="app-connect-metadata">
									<span className="app-connect-canva-logo">
										<Icon name={IconSVG.Canva} size={32} />
									</span>
									<div>
										<div>Canva</div>
										<div>Media library</div>
									</div>
								</div>
								<DisconnectSwitch
									connected={canvaStatus.data.connected}
									loading={canvaDisconnect.isPending}
									onDisconnect={async () => await canvaDisconnect.mutateAsync()}
								/>
							</div>
							<div className="app-connect-spacer" />
							{canvaStatus.data.connected && canvaStatus.data.tokenExpired ? (
								<>
									<CanvaSettingsPanel status={canvaStatus.data} />
									<Button onClick={canvaConnect} full>
										Reconnect
									</Button>
								</>
							) : canvaStatus.data.connected ? (
								<CanvaSettingsPanel status={canvaStatus.data} />
							) : (
								<Button onClick={canvaConnect} full>
									Connect
								</Button>
							)}
						</div>
					)}

					{isHeyGenEnabled && (
						<Suspense
							fallback={<div className="app-connect">Loading HeyGen...</div>}
						>
							<HeyGenAppsCard />
						</Suspense>
					)}

					<VideoCreationCard />
				</div>
			</Col>
		</Row>
	);
};
